import { useMemo, useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import { format, parseISO } from 'date-fns';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import { useStore } from 'react-redux';
import { defaultMemoize } from 'reselect';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { fetchOrderHistory, segmentAnalyticsEvent } from 'src/app/state/actions';
import {
  getActiveMedicationItems,
  getAllBFFOrders,
  getBFFCartDeliveryWindow,
  getBFFCartSubtotal,
  getBFFCartTip,
  getBFFCartTotal,
  getCheckoutDeliveryWindowTimes,
  getCheckoutPatientMedicationIds,
  getCurrentCartId,
  getCurrentPathname,
  getHasFetchedOrders,
  getIsFetchingBFFOrders,
  getNumPendingTransfers,
  getPatients,
  getPotentialTransferItems,
  getPreviousPathName,
  getPromoCodeOnboarding,
  getUnarchivedMedications,
  isLoggedIn,
} from 'src/app/state/selectors';
import { useDispatch } from 'src/app/state/store';
import type { RootState } from 'src/app/state/store';
import type { HomeItem, HomePrescriptionItem } from 'src/app/types';
import { formatDeliveryWindowTimeRange, isSameDayWindowAvailable } from 'src/app/utils';
import { HomeProductStatus } from 'src/consts';

import { convertNumberToValueInCents } from '../components/Pages/Checkout/utils';

function makeStateToPropsSelectorMap(): { [property_name: string]: (state: any) => any } {
  const _getReadyToCheckoutCountForPatient = (homescreenMedications: HomePrescriptionItem[]) => {
    return homescreenMedications?.reduce((count, { status }) => {
      if (status === HomeProductStatus.READY_TO_CHECKOUT) return count + 1;
      return count;
    }, 0);
  };

  const getReadyToCheckOutCount: (homeItems: HomeItem[]) => number = defaultMemoize(homeItems => {
    return homeItems?.reduce((count, { items }) => {
      return count + _getReadyToCheckoutCountForPatient(items);
    }, 0);
  });

  const getAvailableActionsCountForPatient = (homescreenMedications: HomePrescriptionItem[]) => {
    return homescreenMedications?.reduce((count, { cta }) => {
      if (cta) return count + 1;
      return count;
    }, 0);
  };

  const getAvailableActionsCount: (homeItems: HomeItem[]) => number = defaultMemoize(homeItems => {
    return homeItems?.reduce((count, { items }) => {
      return count + getAvailableActionsCountForPatient(items);
    }, 0);
  });

  /**
   * return an object with keys
   */
  return {
    // View Medications
    ready_to_check_out_count: (state: any) => {
      const homescreenItems = getUnarchivedMedications(state);
      const readyToCheckOutCount = getReadyToCheckOutCount(homescreenItems);
      return readyToCheckOutCount;
    },
    pending_requests: (state: any) => {
      const pending_requests = getNumPendingTransfers(state);
      return pending_requests;
    },
    potential_transfers: (state: any) => {
      const potentialTransferItems = getPotentialTransferItems(state);
      return potentialTransferItems?.length ?? 0;
    },
    active_medications: (state: any) => {
      const activeMedicationItems = getActiveMedicationItems(state);
      return activeMedicationItems?.length ?? 0;
    },
    has_dependents: (state: any) => {
      const allPatients = getPatients(state);
      return allPatients?.length > 1;
    },
    available_actions: (state: any) => {
      const homescreenItems = getUnarchivedMedications(state);
      const availableActions = getAvailableActionsCount(homescreenItems);
      return availableActions;
    },
    //View home page (different from below, which is checkout meds)
    patient_medications_id: (state: any) => {
      const activeMedicationItems = getActiveMedicationItems(state);
      const patientMedIds = activeMedicationItems?.map(item => item.patientMedicationId);
      return patientMedIds;
    },
    // View Checkout
    cart_id: (state: any) => getCurrentCartId(state),
    // View Checkout
    patientmedication_ids: (state: any) => getCheckoutPatientMedicationIds(state),
    // View Checkout
    tip_amount: (state: any) => convertNumberToValueInCents(getBFFCartTip(state)),
    total: (state: any) => convertNumberToValueInCents(getBFFCartTotal(state)),
    // View Checkout
    subtotal: (state: any) => convertNumberToValueInCents(getBFFCartSubtotal(state)),
    first_time_order: (state: any) => {
      //todo ensure orders have been fetched before this runs
      const orders = getAllBFFOrders(state);
      return size(orders) === 0;
    },
    // View Delivery Window
    selected_date: (state: any): string => {
      const selectedDeliveryWindow = getBFFCartDeliveryWindow(state);
      return selectedDeliveryWindow
        ? format(parseISO(selectedDeliveryWindow?.startDate), 'MM/dd/yyyy')
        : 'No Date Selected';
    },
    selected_window: (state: any): string => {
      const selectedDeliveryWindow = getBFFCartDeliveryWindow(state);
      if (selectedDeliveryWindow) {
        return formatDeliveryWindowTimeRange(
          selectedDeliveryWindow?.startDate,
          selectedDeliveryWindow?.endDate,
          selectedDeliveryWindow?.category,
          selectedDeliveryWindow?.facilityTimezone
        );
      } else {
        return 'No Window Selected';
      }
    },
    same_day_availability: (state: RootState): boolean => {
      const availableDeliveryWindows = getCheckoutDeliveryWindowTimes(state);
      return isSameDayWindowAvailable(availableDeliveryWindows);
    },
    windows_available_count: (state: RootState): number => getCheckoutDeliveryWindowTimes(state)?.length,
    /** Date + time string? */
    earliest_window: (state: any): string => {
      const earliestWindow = sortBy(getCheckoutDeliveryWindowTimes(state), ['startDate'])[0];

      if (earliestWindow) {
        return formatDeliveryWindowTimeRange(
          earliestWindow?.startDate,
          earliestWindow?.endDate,
          earliestWindow?.category,
          earliestWindow?.facilityTimezone
        );
      } else {
        return 'No Available Windows';
      }
    },
    /** the previous page */
    source: (state: any): string => getPreviousPathName(state) ?? 'None',
    /** the current page */
    pagepath: (state: any): string => getCurrentPathname(state),
    preferred_location: (state: any): string => state?.location?.preferredLocation,
    /** promotional partnerships */
    partnership: (state: any): string => getPromoCodeOnboarding(state),
  };
}

export const useSegmentTrack = <U extends UnifiedEvent>(event: U) => {
  const store = useStore();
  const state = store.getState();
  const dispatch = useDispatch();
  const isUserLoggedIn = isLoggedIn(state);
  console.log('isLoggedIn', isUserLoggedIn);
  const { name } = event;
  const properties = event.properties as unknown as string[];

  const stateToPropsSelectorMap = useMemo(makeStateToPropsSelectorMap, []);
  const [hasFiredEvent, setHasFiredEvent] = useState(false);

  useEffect(() => {
    if (!hasFiredEvent) {
      const state = store.getState();
      const missingProps: (string | never)[] = [];
      const props = properties.reduce((acc: { [key: string]: string }, key: string) => {
        if (stateToPropsSelectorMap[key]) {
          acc[key] = stateToPropsSelectorMap[key](state);
        } else {
          missingProps.push(key);
        }
        return acc;
      }, {});

      if (missingProps.length > 0) {
        const missingPropsStr = missingProps.map(key => `• ${key}`).join('\n');
        Sentry.captureMessage(
          `useSegmentTrack(${name})\n` +
            `Missing or misspelled keys in the stateToPropsSelectorMap.\n` +
            `${missingPropsStr}`
        );
      }

      const hasFetchedOrders = getHasFetchedOrders(state);
      const isFetchingOrders = getIsFetchingBFFOrders(state);

      if (!(hasFetchedOrders || isFetchingOrders) && properties.includes('first_time_order')) {
        dispatch(fetchOrderHistory());
      }

      dispatch(segmentAnalyticsEvent(name, { ...props }));
      setHasFiredEvent(true);
    }
  }, [dispatch, name, properties, store, stateToPropsSelectorMap, hasFiredEvent, isUserLoggedIn]);
};
